import { ChevronDown } from 'lucide-react'
import {
  type InputHTMLAttributes,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react'
import { cn } from '../cn'
import { controlClasses, Field, type FieldBaseProps, FieldError } from './Field'

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'required'>

export interface InputProps extends FieldBaseProps, NativeInput {
  inputClassName?: string
  ref?: Ref<HTMLInputElement>
}

export function Input({
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  id,
  inputClassName,
  type = 'text',
  ...rest
}: InputProps) {
  return (
    <Field {...{ label, hint, error, required, hideLabel, className, id }}>
      {(control) => (
        <input
          type={type}
          className={cn(controlClasses, 'h-11', inputClassName)}
          {...control}
          {...rest}
        />
      )}
    </Field>
  )
}

export type DateInputProps = Omit<InputProps, 'type'>

/** Native date picker (`YYYY-MM-DD` value). */
export function DateInput(props: DateInputProps) {
  return <Input {...props} type="date" inputClassName={cn('tabular-nums', props.inputClassName)} />
}

export interface TextareaProps
  extends FieldBaseProps,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'required'> {
  ref?: Ref<HTMLTextAreaElement>
}

export function Textarea({
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  id,
  rows = 4,
  ...rest
}: TextareaProps) {
  return (
    <Field {...{ label, hint, error, required, hideLabel, className, id }}>
      {(control) => (
        <textarea
          rows={rows}
          className={cn(controlClasses, 'min-h-24 resize-y py-2.5 leading-relaxed')}
          {...control}
          {...rest}
        />
      )}
    </Field>
  )
}

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps
  extends FieldBaseProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'required' | 'children'> {
  options: SelectOption[]
  /** Adds a first empty option ("Choose a portfolio"). */
  placeholder?: string
  ref?: Ref<HTMLSelectElement>
}

export function Select({
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  id,
  options,
  placeholder,
  ...rest
}: SelectProps) {
  return (
    <Field {...{ label, hint, error, required, hideLabel, className, id }}>
      {(control) => (
        <div className="relative">
          <select
            className={cn(controlClasses, 'h-11 cursor-pointer appearance-none pr-9')}
            {...control}
            {...rest}
          >
            {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-sage"
          />
        </div>
      )}
    </Field>
  )
}

export interface CheckboxProps
  extends Omit<FieldBaseProps, 'hideLabel'>,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'required' | 'type'> {
  ref?: Ref<HTMLInputElement>
}

export function Checkbox({ label, hint, error, required, className, id, ...rest }: CheckboxProps) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const hintId = hint ? `${fieldId}-hint` : undefined
  const errorId = error ? `${fieldId}-error` : undefined
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-start gap-2.5">
        <input
          id={fieldId}
          type="checkbox"
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          className="mt-0.5 size-[18px] shrink-0 cursor-pointer rounded-xs border-line-strong accent-teal"
          {...rest}
        />
        <label htmlFor={fieldId} className="cursor-pointer text-sm leading-snug text-ink">
          {label}
          {required ? (
            <span aria-hidden="true" className="ml-0.5 text-attention">
              *
            </span>
          ) : null}
        </label>
      </div>
      {hint ? (
        <p id={hintId} className="pl-7 text-xs leading-relaxed text-sage">
          {hint}
        </p>
      ) : null}
      {error ? (
        <div className="pl-7">
          <FieldError id={errorId}>{error}</FieldError>
        </div>
      ) : null}
    </div>
  )
}
